import { DataTable } from "@bezoned/shared/components/data-table.jsx";
import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import type { TenantRosterUser } from "@bezoned/shared/types/api/tenant/tenant-roster-get.types.js";
import type { SortingState } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";

import { getRosterColumns } from "./roster-table-columns.js";
import { rosterGlobalFilter } from "./roster-table-utils.js";

type CurrentTeam = NonNullable<TenantRosterUser["currentTeam"]>;

interface RosterTableProps {
  currentTeamId: string | undefined;
  onOfficeClick: (args: {
    teamId: CurrentTeam["id"];
    teamName: CurrentTeam["name"];
  }) => void;
  onUserClick: (args: { msUserId: string; anchorRect: DOMRect }) => void;
  users: TenantRosterUser[];
}

const TICK_INTERVAL_MS = 60_000;
const PAGE_SIZE = 10;

const DEFAULT_SORTING: SortingState = [{ desc: false, id: "name" }];

export const RosterTable = ({
  currentTeamId,
  onOfficeClick,
  onUserClick,
  users,
}: RosterTableProps) => {
  const [tickCounter, setTickCounter] = useState(0);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setTickCounter((currentTick) => currentTick + 1);
    }, TICK_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, []);

  // Lint exception: Dependency is required
  const currentTime = useMemo(() => new Date(), [tickCounter]);

  const columns = useMemo(
    () =>
      getRosterColumns({
        currentTeamId,
        currentTime,
        onOfficeClick,
        onUserClick,
        tickCounter,
      }),
    [currentTeamId, currentTime, onOfficeClick, onUserClick, tickCounter]
  );
  const handleGetRowId62 = useStableCallback((row) => row.msUserId);

  return (
    <DataTable
      ariaLabel="People Table"
      columns={columns}
      data={users}
      enablePagination={users.length > PAGE_SIZE}
      getRowId={handleGetRowId62}
      globalFilterFn={rosterGlobalFilter}
      initialPageSize={PAGE_SIZE}
      initialSorting={DEFAULT_SORTING}
      labels={{
        nextPage: "Next",
        pageSummary: ({ pageIndex, pageSize, totalRows }) =>
          `Page ${pageIndex + 1} of ${Math.max(
            1,
            Math.ceil(totalRows / pageSize)
          )}`,
        previousPage: "Previous",
        searchInputAriaLabel: "Search people",
        searchPlaceholder: "Search",
      }}
      noDataState="No people found."
      noSearchResultsState="No matching people found."
      pageSizeOptions={[PAGE_SIZE]}
    />
  );
};
